<script lang="ts">
    import { getContext, onDestroy, onMount } from 'svelte'
    import type { Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { b64dataToBytes, bytesToB64data } from 'hydria/utils/byte_utils'

    interface Props {
        tokenExpired: () => void
        saved: () => void
        close: () => void
    }

    let { tokenExpired, saved, close }: Props = $props()

    let stoken = getContext('securityToken') as Writable<string>

    let passkeyReg: PublicKeyCredentialCreationOptions

    let loading = $state(true)
    let saving = $state(false)

    let refId: string

    let addedModalOpen = false
    if (!document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.add('modal-open')
        addedModalOpen = true
    }

    async function loadReg() {
        loading = true
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('GET', '/json/security/1/passkey/registration', undefined, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }

        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            return
        }

        refId = r.data.passkey_ref_id

        let pkreg = r.data.passkey_reg
        // Load up bas64'ed stuff from python
        pkreg.challenge = await b64dataToBytes(pkreg.challenge)
        pkreg.user.id = await b64dataToBytes(pkreg.user.id)

        passkeyReg = pkreg as PublicKeyCredentialCreationOptions

        loading = false
    }

    async function add() {
        saving = true

        let pkeyCred
        try {
            pkeyCred = await navigator.credentials.create({ publicKey: passkeyReg })
        } catch (e: any) {
            console.error('Add passkey aborted', e)
            saving = false
            return
        }
        if (!pkeyCred) {
            saving = false
            return
        }

        const oPkeyCred = {
            id: pkeyCred.id,
            rawId: await bytesToB64data(pkeyCred.rawId),
            response: {
                attestationObject: await bytesToB64data(pkeyCred.response.attestationObject),
                clientDataJSON: await bytesToB64data(pkeyCred.response.clientDataJSON),
                transports: pkeyCred.response.getTransports(),
            },
            type: pkeyCred.type,
            authenticatorAttachment: pkeyCred.authenticatorAttachment,
        }

        let r: SimpleFetchResult

        try {
            r = await simpleFetch('POST', '/json/security/1/passkey', { pkey_cred: oPkeyCred, ref_id: refId }, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }

        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            saving = false
            return
        }
        notify({
            type: 'success',
            title: $_('Passkey'),
            text: $_('La nouvelle clef d’accès est maintenant en fonction.'),
        })

        saved()
        close()
        saving = false
    }

    onMount(() => {
        loadReg()
    })

    onDestroy(() => {
        if (addedModalOpen && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (!saving) {
                close()
            }
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header text-bg-primary"><div>{$_('Ajouter une clef d’accès')}</div></div>
            <div class="modal-body">
                <p>{$_('Une clé d’accès (« Passkey ») est un identifiant validant votre identité à l’aide de votre empreinte digitale, reconnaissance faciale, un mot de passe d’appareil, ou d’un code.')}</p>
                <p>{$_('Une telle clé peut être utilisée comme remplacement à votre mot de passe et des méthodes d’authentification à deux facteurs.')}</p>
                <p>
                    <button
                        type="button"
                        class="btn btn-primary"
                        onclick={() => {
                            add()
                        }}
                        disabled={loading || saving}
                    >
                        {#if saving}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {$_('Un moment…')}
                        {:else}{$_('Ajouter une clef d’accès (« Passkey »)')}
                        {/if}
                    </button>
                </p>
            </div>
            <div class="modal-footer text-bg-light">
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    disabled={saving || loading}
                    onclick={() => {
                        close()
                    }}>{$_('Cancel')}</button
                >
                <button
                    type="button"
                    class="btn btn-primary"
                    onclick={() => {
                        add()
                    }}
                    disabled={loading || saving}
                >
                    {#if saving}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {$_('Un moment…')}
                    {:else}{$_('Ajouter')}
                    {/if}
                </button>
            </div>
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            if (!loading && !saving) close()
        }}
        aria-hidden="true"
    ></div>
</div>
